/** Domain services: add-on catalogue, TLD settings, parking name servers, protected domains. */
export const services = {
  crumbs: { business: 'Business', settings: 'Integrations', compliance: 'Compliance' },
  common: {
    refresh: 'Refresh',
    reason: 'Reason',
    yes: 'Yes',
    no: 'No',
    none: '—',
    days: '{{count}} d',
    years: '{{count}} y',
    save: 'Save',
    edit: 'Edit',
    loadFailed: 'Could not load the data',
    effectiveFrom: 'Effective from',
    effectiveFromHint: 'Empty = now. A later date schedules the version.',
    done: 'Saved.',
  },
  enum: {
    billing: { ONE_TIME: 'Once per operation', RECURRING: 'Per year × years' },
    mode: { MANDATORY: 'Mandatory', DEFAULT: 'Added by default (removable)', OPTIONAL: 'Ordered by the customer', OFF: 'Not offered' },
    tldClass: { VN: '.vn domains', GTLD: 'gTLD', NEW_GTLD: 'New gTLD', CCTLD: 'Other ccTLD', OTHER: 'Other' },
    ruleType: { FIXED_PRICE: 'Fixed price', MARKUP_FIXED: 'Cost + fixed amount', MARKUP_PERCENT: 'Cost + %' },
    apply: { IMMEDIATE: 'Applied when ordered', MANUAL: 'Applied by staff / a request' },
    tax: { VAT: 'VAT', KCT: 'Not subject to VAT (KCT)' },
    lineKind: { REGISTRY_FEE: 'Registry fee', REGISTRY_MAINTENANCE: 'Maintenance fee', SERVICE: 'Service fee', ADDON: 'Add-on' },
    operation: { REGISTER: 'Register', RENEW: 'Renew', TRANSFER: 'Transfer in', RESTORE: 'Restore', ADDON: 'Add-on' },
    addonState: { ORDERED: 'Ordered (funds held)', ACTIVE: 'Active', CANCELLED: 'Cancelled', ENDED: 'Ended' },
    transferBasis: { FREE: 'Free', RENEWAL: 'Renewal price × years', OWN_PRICE: 'Own transfer price' },
    restoreBasis: { FREE: 'Free', RENEWAL: 'Renewal price × years', OWN_PRICE: 'Own restore price', OWN_PRICE_PLUS_RENEWAL: 'Restore price + renewal' },
    onExpiry: { PARKING_NS: 'Parking name servers', REGISTRY_HANDLES: 'Registry handles it', CLIENT_HOLD: 'clientHold', NONE: 'Nothing' },
    pendingStatus: { PENDING: 'Awaiting confirmation', CONFIRMED: 'Confirmed', REJECTED: 'Rejected', EXECUTED: 'Executed', FAILED: 'Failed' },
    changeType: {
      NAMESERVERS: 'Name servers',
      DNSSEC: 'DNSSEC',
      TRANSFER_LOCK: 'Transfer lock',
      AUTH_INFO: 'Transfer code',
      RENEW: 'Renewal',
      DELETE: 'Deletion',
      TRANSFER_OUT_APPROVE: 'Approve transfer out',
      REGISTRANT_CHANGE: 'Registrant change',
      ADDON_ORDER: 'Order a service',
      ADDON_CANCEL: 'Cancel a service',
      RESTORE: 'Restore',
    },
    attentionKind: { PROTECTED_CHANGE_PENDING: 'Change awaiting confirmation', PROTECTED_DOMAIN_EXPIRY: 'Expiry stage', PROTECTED_TRANSFER_OUT: 'Transfer-out request' },
    parkingState: { PARKING: 'Parking', PARKED: 'Parked', UNPARKING: 'Restoring', UNPARKED: 'Restored', ABANDONED: 'Abandoned' },
    ncCase: { HOLDER_TRANSFER: 'Holder transfer', SUBJECT_CONVERSION: 'Subject conversion', NAME_CORRECTION: 'Name correction' },
    ncSubCase: {
      GIFT_CONTRIBUTION_INHERITANCE: 'Gift, contribution, inheritance',
      AUTHORITY_RENAME: 'Organisation renamed (enterprise code kept)',
      DIVISION_MERGER: 'Division, merger, consolidation',
      REORGANISATION_SALE: 'Reorganisation, sale, contribution',
    },
    ncStatus: {
      DRAFT: 'Collecting documents',
      SUBMITTED: 'Under review',
      CORRECTION_REQUESTED: 'Correction requested',
      APPROVED: 'Approved',
      REGISTRY_CONTACT: 'Creating the VNNIC contact',
      PANEL_PENDING: 'Waiting for the VNNIC panel',
      COMPLETED: 'Completed',
      REJECTED: 'Rejected',
      CANCELLED: 'Cancelled',
    },
    ncStage: { BEFORE_APPROVAL: 'Before approval', AFTER_APPROVAL: 'After approval' },
    ncCheck: {
      NOT_PROTECTED: 'Not a protected domain',
      NO_LEGAL_LOCK: 'No legal lock',
      NO_OPEN_DISPUTE: 'No open dispute / complaint',
      NOT_SUSPENDED: 'Not suspended',
      DOMAIN_STATE: 'Domain state allows it',
      SAME_HOLDER: 'Same holder',
      CONSENT_BOTH_HOLDERS: 'Both holders agreed',
      NEW_HOLDER_VERIFIED: 'New holder verified (VN_SUBJECT_VERIFICATION)',
      DOCUMENTS_BEFORE_APPROVAL: 'Documents before approval',
      ENTERPRISE_CODE_MATCH: 'Enterprise code unchanged',
      DECLARATION_SIGNED: 'Declaration under the new name signed',
    },
    checkStatus: { PASS: 'Passed', FAIL: 'Failed', WARN: 'Warning' },
    ncDecision: { APPROVE: 'Approve', REQUEST_CORRECTION: 'Request a correction', REJECT: 'Reject' },
    ncEvent: {
      OPENED: 'Opened',
      STATUS: 'Status',
      DOCUMENT_UPLOADED: 'Document uploaded',
      CONSENT_RECORDED: 'Consent recorded',
      SUBMITTED: 'Submitted for review',
      WAITING_DOCUMENTS: 'Waiting for documents',
      CONTACT_CREATED: 'Contact created at VNNIC',
      PANEL_SUBMITTED: 'Entered in the VNNIC panel',
      LOCKED: 'Domain locked (client transfer / update / delete)',
      LOCK_FAILED: 'Locking the domain failed',
      DECLARATION_ATTACHED: 'Declaration under the new name generated',
      SPELLING_CORRECTION_MARKED: 'Staff confirmed a spelling correction only',
    },
    consentSide: { CURRENT_REGISTRANT: 'Current holder', NEW_REGISTRANT: 'New holder' },
    consentMethod: { IN_PERSON: 'In person', SIGNED_DOCUMENT: 'Signed document', DIGITAL_SIGNATURE: 'Digital signature', EMAIL_LINK: 'E-mail link', PHONE_CALL: 'Phone call' },
    signedBy: { BOTH_PARTIES: 'Both parties', TRANSFEROR: 'Transferor', TRANSFEREE: 'Transferee', HOLDER: 'Holder', NEW_HOLDER: 'New holder', AUTHORITY: 'Competent authority' },
    panelStatus: { PENDING: 'To enter in the panel', SUBMITTED: 'Entered, waiting for VNNIC', COMPLETED: 'Done at VNNIC', REFUSED: 'Refused by VNNIC', CANCELLED: 'Cancelled' },
    panelOperation: { NAME_CHANGE: 'Name change', REGISTRY_LOCK: 'Registry lock', REGISTRY_UNLOCK: 'Registry unlock' },
    panelExecutor: { MANUAL_PANEL: 'Staff in the VNNIC panel', VNNIC_API: 'VNNIC API' },
  },
  catalogue: {
    title: 'Domain services',
    subtitle: 'The add-on catalogue (including the VNNIC fees and the ICANN fee) and the lifecycle services. Offer add-ons and set their price per TLD on the TLD page (Prices & services tab), the only place they are edited.',
    fileTitle: 'Service catalogue',
    fileBody: 'Catalogue version {{version}}, updated by the engineering team. Read-only here; turn services on and price them on each TLD page.',
    lifecycle: 'Lifecycle services',
    lifecycleHint: 'Add-ons that can come with each operation (mandatory or added by default on at least one TLD).',
    col: {
      operation: 'Service',
      rules: 'Price rules in force',
      components: 'Registry fee lines',
      addons: 'Add-ons charged with it',
      code: 'Code',
      name: 'Service',
      billing: 'Billing',
      operations: 'Operations',
      apply: 'Applied',
      tax: 'Tax',
      hints: 'Default by TLD class',
      tlds: 'Offered on (* mandatory)',
      prices: 'Prices per TLD',
      domains: 'Domains using it',
      state: 'State',
    },
    billingHint: { ONE_TIME: 'Charged once per operation.', RECURRING: 'Multiplied by the years of the operation (register / renew N years → N times).' },
    addons: 'Add-on catalogue',
    addonsHint: 'There is no price for every TLD: each TLD has its own. A mandatory add-on without a price blocks quoting that TLD.',
    noTld: 'Not offered on any TLD',
    percentOfCost: '{{value}} % of cost',
    allTlds: 'Every TLD',
    free: 'Free',
    noPrice: 'No price yet',
    inactive: 'Inactive',
    components: 'Registry cost components (legacy)',
    componentsHint: 'Still in force from before the fees became mandatory add-ons. Replace them with a mandatory add-on and a price per TLD.',
    col2: { tld: 'TLD', operation: 'Operation', component: 'Component', kind: 'Line', amount: 'Amount', perYear: 'Per year', tax: 'Tax', from: 'From' },
  },
  tldAddons: {
    title: 'Services of {{zone}}',
    subtitle: 'Which services this TLD offers and how; prices are set on the Prices tab.',
    applyDefaults: 'Turn on the default services',
    applyDefaultsDescription: "Turns on the default services for {{cls}} TLDs not yet set on this TLD (fees an upstream registrar's price already includes are left out). Services already set stay as they are.",
    appliedDefaults: '{{count}} add-on(s) offered.',
    appliedNone: 'Nothing to add.',
    col: { service: 'Service', mode: 'On this TLD', hint: 'Default', ops: 'Operations', billing: 'Billing', tax: 'Tax', price: 'Price', domains: 'Domains' },
    change: 'Change',
    changeTitle: '{{name}} on {{zone}}',
    changeDescription: 'Mandatory: added to every quote of its operations; the customer cannot remove it. Added by default: included at registration / transfer-in, removable. Ordered by the customer: only when ordered.',
    modeField: 'Offered as',
    missingPrice: 'No price: quotes are refused',
    priced: 'Priced',
    noPriceNeeded: '—',
    history: 'Change history',
    noHistory: 'No changes yet.',
    offered: '{{count}} add-on(s) offered',
    mandatoryMissing: '{{count}} mandatory add-on(s) without a price — quotes for this TLD are refused until priced.',
    empty: 'This TLD offers no service yet. Use "Turn on the default services" or turn services on one by one.',
  },
  tldPrices: {
    addons: 'Add-on prices of {{zone}}',
    addonsHint: "This TLD's own prices. Pass-through fees (VNNIC, ICANN) are the add-on's cost: the customer pays exactly that when the price book has no selling price for it.",
    noneOffered: 'This TLD offers no add-on yet (Services tab).',
    col: { service: 'Service', mode: 'On this TLD', cost: 'Cost / pass-through fee', sell: 'Selling price per price book', actions: '' },
    setCost: 'Set price',
    setSell: 'Selling price',
    atCost: 'None: charged at cost',
    missing: 'No price',
    percent: "{{value}} % of the operation's cost",
    forOp: 'on {{op}}',
    breakdown: 'Price per operation',
    breakdownHint: 'As a quote for this TLD would be: service fee + mandatory / default add-ons = total, each line with its tax.',
    book: 'Price book',
    years: 'Years',
    line: { kind: 'Line', item: 'Item', amount: 'Before tax', tax: 'Tax', total: 'Total' },
    total: 'Total',
    cannotPrice: 'Cannot be priced',
  },
  tld: {
    title: 'TLD settings',
    subtitle: 'Per-TLD transfer-in, restore and expiry terms, versioned with an effective date and a reason. TLDs without settings follow the expiration policy files.',
    parking: 'Parking name servers',
    parkingHint: 'At expiry, TLDs with "parking name servers" switch to this pair (the saved name servers and DS come back on renewal during grace).',
    editParking: 'Change parking pair',
    parkingField: 'Name servers (one per line)',
    defaultNs: 'Default name servers for new registrations',
    defaultNsHint: 'A registration ordered without name servers gets these (registrations already ordered keep theirs).',
    editDefaultNs: 'Change default name servers',
    nsTooFew: 'Enter 2 to 13 name servers.',
    col: { zone: 'TLD', transfer: 'Transfer in', restore: 'Restore', stages: 'Grace / redemption / pending delete', onExpiry: 'At expiry', autoRenew: 'Registry auto-renew', version: 'Version', state: 'State' },
    policyFiles: 'Policy files',
    notAllowed: 'Not allowed',
    addsYears: '+{{count}} y',
    argp: 'ARGP {{argp}} d, delete {{margin}} d before its end',
    stageSkipped: '0 = skipped',
    termsTitle: 'Lifecycle & transfer / restore of {{zone}}',
    termsSubtitle: "This TLD's own terms; each change is a new version with an effective date and a reason.",
    inForceSince: 'Version {{version}} in force since {{from}} — {{reason}}',
    noSettingsTitle: 'No terms of its own',
    noSettings: 'This TLD follows the default expiration / transfer policy files. Use "Edit settings" to give it its own terms.',
    scheduledNote: '{{count}} version(s) scheduled for later.',
    versions: 'Versions',
    noVersions: 'No version yet.',
    inForce: 'In force',
    scheduled: 'Scheduled',
    superseded: 'Superseded',
    skipped: 'stage skipped',
    parkingShared: 'The parking name-server pair is shared by every TLD whose DNS at expiry is "parking name servers".',
    parkingWhere: 'Changed in Settings › Domains & TLDs',
    sections: { transfer: 'Transfer-in', restore: 'Restore', expiry: 'Expiry', reminders: 'Reminders and auto-renew' },
    edit: 'Edit settings',
    editTitle: 'TLD settings · .{{zone}}',
    errp: 'gTLD: the ICANN ERRP minimums are checked (DNS interrupted ≥ 8 days before deletion, 30-day redemption with restore, reminders ~1 month and ~1 week before and within 5 days after).',
    form: {
      transferAllowed: 'Transfer in allowed',
      transferYears: 'Years added',
      transferMax: 'Customer may choose up to (years)',
      transferBasis: 'Transfer price',
      restoreAllowed: 'Restore allowed',
      restoreYears: 'Years added by a restore',
      restoreBasis: 'Restore price',
      grace: 'Grace — renewable (days)',
      redemption: 'Redemption — restorable (days)',
      pendingDelete: 'Pending delete (days)',
      before: 'Reminders before expiry (days, comma separated)',
      after: 'Reminders after expiry (days)',
      lead: 'Auto-renew lead (days)',
      onExpiry: 'DNS at expiry',
      autoRenews: 'The registry renews at expiry by itself',
      argp: 'Registry auto-renew grace (days)',
      margin: 'Delete this many days before its end',
    },
  },
  protected: {
    title: 'Protected domains needing action',
    subtitle: 'Protected domains are never changed automatically: every change waits for a staff confirmation (not by the requester), and expiry stages are handled by hand.',
    protectedCount: 'Protected domains',
    openItems: 'Open items',
    pendingCount: 'Changes awaiting confirmation',
    attention: 'Needs action',
    pending: 'Pending changes',
    col: { domain: 'Domain', kind: 'What', summary: 'Details', opened: 'Opened', state: 'Lifecycle', expires: 'Expires', change: 'Change', requested: 'Requested by', at: 'At', status: 'Status', actions: 'Actions' },
    confirm: 'Confirm',
    confirmTitle: 'Confirm and execute the change',
    confirmWarning: 'The change is sent to the registry now. You cannot confirm your own request.',
    reject: 'Reject',
    rejectTitle: 'Reject the change',
    executed: 'Done: {{status}}',
    rejected: 'Rejected.',
    empty: 'Nothing needs action.',
  },
  domain: {
    title: 'Services in use',
    subtitle: 'Add-on services of this domain. Mandatory services cannot be cancelled.',
    col: { service: 'Service', state: 'State', period: 'Period', billing: 'Billing', actions: '' },
    mandatory: 'Mandatory',
    empty: 'No services on this domain.',
    order: 'Order a service',
    orderTitle: 'Order a service for this domain',
    orderHint: 'Charged to the domain\'s customer account (funds held until the service is applied).',
    service: 'Service',
    yearsField: 'Years (yearly services; empty = until the domain expires)',
    ordered: 'Ordered: {{state}}',
    awaiting: 'The domain is protected: the order waits for staff confirmation.',
    apply: 'Apply',
    applyTitle: 'The service was carried out: charge it',
    cancel: 'Cancel',
    cancelTitle: 'Cancel the service',
    cancelWarning: 'An ordered service releases the held funds; an active one ends without refund.',
    protection: 'Protected domain',
    protectedOn: 'Protected: every change waits for staff confirmation; nothing happens automatically at expiry.',
    protectedOff: 'Not protected.',
    protect: 'Protect',
    protectTitle: 'Protect this domain',
    unprotect: 'Remove protection',
    unprotectTitle: 'Remove the protection',
    pending: 'Changes awaiting confirmation',
    parking: 'Parking at expiry',
    parkingInfo: '{{state}} on {{servers}}; saved: {{saved}} name server(s), {{ds}} DS record(s).',
    panelPending: { REGISTRY_LOCK: 'Waiting for the VNNIC panel (lock)', REGISTRY_UNLOCK: 'Waiting for the VNNIC panel (unlock)' },
    panelOpen: 'VNNIC panel operations',
  },
  nameChange: {
    title: 'Name changes',
    subtitle: '.vn name changes and holder transfers: documents per requirement set, review within 10 days, then the VNNIC panel step.',
    requests: 'Requests',
    empty: 'No request.',
    overdue: 'overdue',
    stats: { toReview: 'To review', overdue: 'Review overdue', panel: 'Waiting for the VNNIC panel' },
    filter: { status: 'Status', case: 'Case', any: 'Any', open: 'Open requests only' },
    col: {
      domain: 'Domain',
      case: 'Case',
      holders: 'Holder → new holder',
      status: 'Status',
      due: 'Answer by',
      panel: 'VNNIC panel',
      requested: 'Requested',
      document: 'Document',
      stage: 'When',
      required: 'Required',
      template: 'Template',
      signedBy: 'Signed by',
      present: 'Uploaded',
      check: 'Check',
      result: 'Result',
      blocking: 'Blocks approval',
      detail: 'Detail',
      file: 'File',
      by: 'By',
      at: 'At',
      event: 'Event',
      note: 'Note',
    },
    formHint: 'VNNIC changes a registrant\'s legal name only in its panel. The request collects the documents of its case, is reviewed, then the new contact is created at VNNIC and staff enter the change in the VNNIC panel.',
    case: 'Case',
    subCase: 'Sub-case',
    toSubject: 'New holder (subject)',
    toSubjectHint: 'A subject of the same customer account; create it first if needed.',
    toSubjectSame: 'A name correction keeps the holder: the current subject with its corrected new version.',
    note: 'Note',
    loading: 'Loading…',
    choose: 'Choose…',
    close: 'Close',
    submitOpen: 'Open the request',
    opened: 'The request is open.',
    openRequest: 'Open the request',
    summary: 'Request',
    fromHolder: 'Current holder',
    toHolder: 'New holder',
    requirementSet: 'Requirement set',
    newerVersion: '(a newer version {{version}} exists; this request keeps its own)',
    dueAt: 'Answer by (10 days)',
    consentCurrent: 'Consent — current holder',
    consentNew: 'Consent — new holder',
    waiting: 'Waiting',
    addon: 'Add-on (charged when done)',
    newContact: 'New contact ID at VNNIC',
    reviewReason: 'Review',
    closedReason: 'Closed because',
    legalBasis: 'Legal basis',
    checklist: 'Documents (requirement set v{{version}})',
    checks: 'Automatic checks',
    checksAt: 'Evaluated {{at}}',
    noChecks: 'Not evaluated yet.',
    documents: 'Uploaded documents',
    noDocuments: 'No document uploaded.',
    timeline: 'Timeline',
    noEvents: 'Nothing yet.',
    oneOf: 'Yes, or instead: {{others}}',
    or: ' or ',
    present: 'Yes ({{count}})',
    absent: 'Missing',
    upload: 'Upload a document',
    uploadTitle: 'Upload a document of the requirement set',
    documentType: 'Document',
    file: 'File',
    fileHint: 'PDF, JPEG, PNG or XML, at most 20 MB. Stored encrypted with the dossier documents.',
    fileType: 'This file type is not accepted.',
    uploaded: 'Uploaded.',
    recordConsent: 'Record consent',
    consentTitle: 'Record a holder\'s consent obtained by staff',
    side: 'Holder',
    method: 'How it was obtained',
    consentOne: 'Recorded. Waiting for the other holder.',
    consentBoth: 'Both holders agreed.',
    submit: 'Submit for review',
    submitTitle: 'Submit the request for review',
    submitHint: 'The add-on is ordered (funds held) and the review starts: the registrar answers within 10 days of the complete dossier.',
    submitted: 'Submitted. Answer by {{due}}.',
    review: 'Review',
    reviewTitle: 'Review the dossier',
    decision: 'Decision',
    decide: 'Record the decision',
    failingChecks: 'Checks not passed: {{checks}}. The request cannot be approved yet.',
    rejectWarning: 'The request ends; the held funds are released.',
    reviewed: 'Recorded: {{status}}.',
    vnnicRefused: 'VNNIC refused',
    vnnicRefusedTitle: 'Record VNNIC\'s refusal',
    vnnicRefusedWarning: 'The request ends as rejected, the held funds are released and the new contact is left unused.',
    cancel: 'Cancel',
    cancelTitle: 'Cancel the request',
    cancelWarning: 'Possible until the request goes to VNNIC. The held funds are released.',
    closed: 'Closed.',
    waitingAfter: 'Approved. Waiting for the documents required after approval: {{docs}}.',
    waitingBefore: 'Still missing before it can be submitted: {{docs}}.',
    panelHint: 'Enter the change in the VNNIC panel with the new contact ID {{handle}}, then record it as submitted. The request completes when VNNIC\'s domain update message shows the new registrant.',
    panelHintAdmin: 'Organisation: its legal representative is the admin contact {{handle}}.',
    adminContact: 'Admin contact (legal representative)',
    completedVia: 'Completed by',
    via: { POLL: 'VNNIC message', STAFF: 'Staff confirmation', RECONCILIATION: 'Automatic reconciliation' },
    lockStatus: 'Lock after the change',
    lock: { PENDING: 'Waiting for the registry', APPLIED: 'Locked (transfer / update / delete)', REJECTED: 'Refused by the registry — needs attention' },
    confirm: 'Confirm completion',
    confirmTitle: 'Confirm VNNIC applied the name change',
    confirmHint: 'For changes VNNIC handled offline (panel, e-mail, phone). VNNIC\'s domain:info is read first; the request completes only when its registrant is the new contact, then the domain is locked.',
    confirmed: 'Completed: the domain belongs to the new holder and is locked.',
    notYet: 'Not at VNNIC yet: its registrant is {{actual}}, expected {{expected}}. The request stays open.',
    protected: 'The domain is protected: a name change cannot be requested online. Please contact the registrar\'s staff, who handle it manually.',
    choice: {
      ORG_RENAME: 'Organisation renamed (enterprise code kept)',
      HOLDER_TRANSFER: 'Transfer to another holder',
      SUBJECT_CONVERSION: 'Other subject conversion (gift, division, reorganisation…)',
      NAME_CORRECTION: 'Name correction (spelling, an individual\'s legal name change)',
      NAME_CORRECTION_ORG: 'Spelling correction of the organisation\'s name only',
    },
    orgRenameHint: 'An organisation renamed while keeping its enterprise code needs the business registration certificate (amendment) showing the new name, or the competent authority\'s rename decision, and a declaration under the new name for each domain. The old business licence is not needed. The new holder\'s enterprise code must be the current registrant\'s.',
    spellingOnlyHint: 'Only when the organisation\'s name is misspelled. A renamed organisation: choose "Organisation renamed (enterprise code kept)".',
    spellingReason: 'Why this is only a spelling correction',
    spellingReasonHint: 'Required for organisations; kept with the request and in the audit log.',
    toSubjectRename: 'The current holder with a new version carrying the new name, or a holder with the same enterprise code.',
    caseMismatch: 'An organisation renamed while keeping its enterprise code is not a "Name correction": choose "Organisation renamed (enterprise code kept)". A name correction is only for spelling mistakes, with a reason.',
    enterpriseChanged: 'The new holder\'s enterprise code differs from the current registrant\'s, so this is not a rename: choose "Reorganisation, sale, contribution" or "Division, separation, merger, consolidation".',
    spellingMarked: 'Staff confirmed a spelling correction only',
    enterpriseCode: 'Enterprise code',
    ec: { MATCH: 'Match', MISMATCH: 'Different', NOT_APPLICABLE: 'None on record' },
    ecHints: '{{current}} → {{next}}',
    ecMismatch: 'The enterprise code changed: do not approve as a rename. Ask the customer to choose "Reorganisation, sale, contribution" or "Division, separation, merger, consolidation".',
    ecNotApplicable: 'The current registrant has no enterprise code on record: compare the competent authority\'s rename decision.',
    declarations: 'Declarations under the new name (per domain)',
    declarationsHint: 'One declaration per domain, generated from the domain and the holder\'s new data. The holder signs it and uploads the signed copy on the holder\'s page; the signed copy must be verified before approval.',
    generateDeclaration: 'Generate the declaration for this domain',
    generateDeclarationTitle: 'Generate the declaration under the new name',
    generateDeclarationHint: 'The declaration is generated from the domain {{domain}} and the holder\'s new data.',
    declarationGenerated: 'Declaration generated.',
    noDeclarations: 'No declaration generated for this domain yet.',
    openHolder: 'Holder page (upload the signed copy)',
    generatedPerDomain: 'Generated per domain',
    declStatus: { GENERATED: 'Generated, awaiting signature', UPLOADED: 'Signed copy uploaded, being checked', VERIFIED: 'Valid digital signature', REVIEW_REQUIRED: 'Signed copy awaiting staff review', APPROVED: 'Signed copy approved by staff', REJECTED: 'Rejected' },
    holderDomains: 'The current holder\'s .vn domains',
    holderDomainsHint: 'A rename applies per domain: each domain has its own request and declaration.',
    noRequest: 'No request yet',
    thisRequest: 'this request',
    col2: { domain: 'Domain', form: 'Form', status: 'Status', generated: 'Generated', request: 'Request', declaration: 'Declaration' },
  },
  panel: {
    title: 'VNNIC panel operations',
    subtitle: 'Changes staff enter in the VNNIC panel (name change, registry lock / unlock). They complete by themselves when VNNIC\'s domain update message and domain:info show them.',
    empty: 'No panel operation.',
    col: { operation: 'Operation', expected: 'VNNIC must show', status: 'Status', since: 'Since', reference: 'VNNIC reference', actions: '' },
    expectRegistrant: 'registrant = {{handle}}',
    expectSet: 'set: {{statuses}}',
    expectCleared: 'cleared: {{statuses}}',
    overdue: 'overdue',
    recordSubmitted: 'Entered in the panel',
    recordSubmittedTitle: 'Record the change as entered in the VNNIC panel',
    reference: 'VNNIC reference',
    referenceHint: 'The request number shown by the VNNIC panel, if any.',
    note: 'Note',
    submittedDone: 'Recorded. The operation completes when VNNIC shows the change.',
    checkNow: 'Check at VNNIC now',
    notYet: 'Not visible at VNNIC yet.',
    refused: 'VNNIC refused',
    refusedTitle: 'Record VNNIC\'s refusal',
    refusedWarning: 'An ordered registry lock is cancelled and its held funds released.',
    refusedDone: 'Recorded.',
    stepTitle: 'VNNIC panel step',
    stepHint: 'Staff enter the change in the VNNIC panel; the domain update message (or the hourly check) completes it.',
    executor: 'Done by',
    submittedAt: 'Entered at',
    lastChecked: 'Last checked',
    observed: 'VNNIC showed',
  },
  requirements: {
    title: 'Name-change documents',
    subtitle: 'The documents each case requires (Decree 115/2025, Circular 48/2025, VNNIC). A change is a new version with a date and a reason; open requests keep the version they were opened under.',
    inForce: 'v{{version}} in force',
    reviewDaysValue: 'Review within {{count}} days',
    newVersion: 'New version',
    newVersionTitle: 'Publish a new version',
    versionHint: 'Prefilled with the latest version. Requests already open are not affected.',
    titleVi: 'Title (Vietnamese)',
    titleEn: 'Title (English)',
    legalBasis: 'Legal basis',
    reviewDays: 'Days to answer',
    documents: 'Documents',
    code: 'Code',
    stage: 'When',
    oneOf: 'Alternative group',
    oneOfHint: 'Documents with the same group: any one is enough (e.g. TAX).',
    nameVi: 'Name (Vietnamese)',
    nameEn: 'Name (English)',
    template: 'Template',
    signedBy: 'Signed by',
    required: 'Required',
    perDomain: 'Declaration generated per domain',
    remove: 'Remove',
    addDocument: 'Add a document',
    publish: 'Publish',
    published: 'Version {{version}} published.',
  },
  tldPricing: {
    advanced: 'Advanced (registry cost, fees & add-ons, prices per price book)',
    title: 'Prices & services of {{zone}}',
    subtitle: 'Everything this TLD charges, in one place: registry cost, mandatory fees, add-ons and selling prices.',
    summary: 'Price per operation',
    summaryHint: 'What a customer of {{book}} pays for 1 year: service fee + mandatory fees = total, each line with its tax.',
    book: 'Price book',
    registryCost: 'Registry cost',
    registryCostNote: 'our cost, not a customer line',
    total: 'Customer pays (1 year)',
    tax: 'Tax',
    cannotPrice: 'Cannot be priced',
    feeMissing: '{{name}}: no price',
    noBooks: 'No active price book: nothing can be priced.',
    costs: 'Registry cost',
    costsHint: 'What the registry charges us per operation and year.',
    noCosts: 'No registry cost for this TLD yet: no operation can be priced.',
    from: 'from {{date}}',
    fees: 'Fees & add-ons',
    feesHint: 'Each service on at this TLD: how it is offered, its cost / pass-through fee and its selling price.',
    col: { service: 'Service', mode: 'Mode', cost: 'Cost / pass-through fee', sell: 'Selling price', actions: '' },
    billing: { ONE_TIME: 'once', RECURRING: 'per year' },
    mode: { MANDATORY: 'Mandatory', DEFAULT: 'Added', OPTIONAL: 'Optional', OFF: 'Off' },
    modeHelp: {
      MANDATORY: 'Charged with every quote of its operations; the customer cannot remove it.',
      DEFAULT: 'Added at registration / transfer-in; the customer can remove it.',
      OPTIONAL: 'Only when the customer orders it.',
      OFF: 'Not offered on this TLD.',
    },
    differs: 'differs from the default',
    differsTitle: 'Default for this kind of TLD: {{hint}}',
    noHint: 'not offered',
    chargedWith: 'Charged with: {{ops}}',
    atCost: '= cost',
    missing: 'No price',
    free: 'Free',
    editPrice: 'Edit price',
    modeTitle: '{{name}} on {{zone}}',
    modeTo: 'New mode: {{mode}}',
    noneOn: 'No service is on for this TLD. Turn them on below or use "Turn on the default services".',
    offList: 'Add-ons not on ({{count}})',
    turnOn: 'Turn on',
    opFees: 'Operation fees',
    opFeesHint: 'Fees of a .vn name-change request: charged when the request is submitted, never with a registration, renewal or transfer.',
    feeFor: 'Fee of: {{case}}',
    on: 'On',
    off: 'Off',
    turnOff: 'Turn off',
    history: 'Change history ({{count}})',
    legacyNote: 'Legacy codes no longer in the catalogue appear only here and in order / invoice history.',
    sell: 'Selling prices (price books)',
    sellHint: 'What a customer of each price book pays for 1 year, before tax and without the mandatory fees. Click a cell to change it.',
    price: {
      title: 'Price of {{name}} on {{zone}}',
      description: 'A new version starts at the chosen time and closes the one in force; quotes already given keep their price.',
      current: 'In force',
      none: 'No price yet.',
      costNew: 'New cost / pass-through fee',
      sellNew: 'New selling price',
      keep: 'Keep',
      set: 'Set',
      amount: 'Amount',
      currency: 'Currency',
      basis: 'Basis',
      bases: { FIXED: 'Fixed amount', PERCENT_OF_REGISTRY_COST: '% of the operation\'s registry cost' },
      forOp: 'For operation',
      everyOp: 'Every operation',
      book: 'Price book',
      ruleType: 'Selling price',
      value: 'Value',
      taxFixed: 'Tax: {{tax}} (from the catalogue)',
      appliesTo: 'Charged with: {{ops}}',
      nothing: 'Choose a new cost, a new selling price or both.',
      save: 'Save price',
      saved: 'Saved.',
      gridDescription: 'The service\'s cost and selling price for every operation in one go. Rows left on "Keep" do not change; the others are saved together from the chosen time. Quotes already given keep their price.',
      costs: 'Cost / pass-through fee per operation',
      sells: 'Selling price per price book',
      sellsHint: 'One selling price for every operation of the service; "Customer pays" is worked out from each operation\'s cost.',
      op: 'Operation',
      method: 'New price',
      preview: 'Customer pays',
      inherit: 'As "Every operation"',
      endCost: 'Remove own cost',
      endRule: 'Sell at cost',
      atCost: 'Sold at cost',
      nothingGrid: 'No row changed yet.',
      saveCount_one: 'Save {{count}} change',
      saveCount_other: 'Save {{count}} changes',
      savedCount_one: 'Saved {{count}} change.',
      savedCount_other: 'Saved {{count}} changes.',
    },
  },
};
